import {
  CircularProgress,
  Divider,
  Grid2 as Grid,
  Typography,
} from "@mui/material";
import { Button, Modal, Tabs } from "../common";
import {
  InvoiceAdjustmentPayload,
  InvoiceOverviewInfo,
  TabItem,
} from "../../types";
import { useEffect, useState } from "react";
import { AdjustmentsForm, InvoiceActions, InvoiceOverview } from "./fragments";
import { InvoiceHistory } from "./fragments/InvoiceHistory";
import { InvoiceNotes } from "./fragments/InvoiceNotes";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  useApplyInvoiceAdjustmentsMutation,
  useFetchInvoiceOverviewQuery,
  useFetchPreviewInvoiceQuery,
} from "../../api/invoiceService";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import {
  isInvoiceActionsMutationsLoadingAtom,
  toastOptionsAtom,
  userAtom,
} from "../../atoms";
import { useAtom } from "jotai";
import { InvoiceStatus, Role } from "../../enums";
import {
  INVOICE_STATUS_TARGET_ACTIONS_ADMIN,
  INVOICE_STATUS_TARGET_ACTIONS_CUSTOMER,
} from "../../constants";
import Loading from "../common/loading/Loading";
import DiscountIcon from "@mui/icons-material/Discount";
import { CreateRegenerateInvoiceModal } from "../common/modal/CreateRegenerateInvoiceModal";

export const InvoiceDetails = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [user] = useAtom(userAtom);
  const params = useParams<{ invoiceNumber: "string" }>();
  const [pdfFileLink, setPdfFileLink] = useState<string>();
  const [queryParams] = useSearchParams();
  const orgId = Number(queryParams.get("orgId")) || 0;
  const accountId = Number(queryParams.get("accountId")) || 0;
  const [selectedTab, setSelectedTab] = useState<number>(1);
  const [requestId, setRequestId] = useState("");
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [invoiceOverview, setInvoiceOverview] = useState<InvoiceOverviewInfo>();
  const [, setToastOptions] = useAtom(toastOptionsAtom);
  const navigate = useNavigate();
  const tabItems: TabItem[] = [
    {
      key: "Overview",
      value: 1,
    },
    {
      key: "History",
      value: 2,
    },
    {
      key: "Notes",
      value: 3,
    },
  ];
  const [isDiscountModalOpen, setIsDiscountModalOpen] = useState(false);

  const fetchInvoiceOverviewQuery = useFetchInvoiceOverviewQuery(
    orgId,
    accountId,
    params.invoiceNumber || "",
  );
  const fetchInvoicePreviewQuery = useFetchPreviewInvoiceQuery(
    orgId,
    accountId,
    params.invoiceNumber || "",
  );

  const handleReturn = () => {
    navigate(-1);
  };

  const handleRefresh = () => {
    fetchInvoiceOverviewQuery.refetch();
  };

  const applyInvoiceAdjustmentsMutation = useApplyInvoiceAdjustmentsMutation();

  const [isInvoiceActionsMutationsLoading] = useAtom(
    isInvoiceActionsMutationsLoadingAtom,
  );

  const handleApplyAdjustments = (data: InvoiceAdjustmentPayload) => {
    applyInvoiceAdjustmentsMutation.mutate({
      orgId,
      accountId,
      invoiceNumber: invoiceOverview?.invoiceNumber || "",
      ...data,
    });
  };

  useEffect(() => {
    if (fetchInvoicePreviewQuery.isSuccess && fetchInvoicePreviewQuery.data) {
      const dataToArrayBuffer = new Uint8Array(fetchInvoicePreviewQuery.data);
      const linkToPdf = URL.createObjectURL(
        new Blob([dataToArrayBuffer], {
          type: "application/pdf",
        }),
      );
      setPdfFileLink(linkToPdf);
    }
  }, [
    fetchInvoicePreviewQuery.isSuccess,
    fetchInvoicePreviewQuery.isLoading,
    fetchInvoicePreviewQuery.data,
  ]);

  useEffect(() => {
    if (fetchInvoiceOverviewQuery.isSuccess) {
      setInvoiceOverview(fetchInvoiceOverviewQuery.data);
    }

    setLoading(
      fetchInvoiceOverviewQuery.isLoading ||
        fetchInvoiceOverviewQuery.isFetching ||
        fetchInvoiceOverviewQuery.isRefetching,
    );
  }, [
    fetchInvoiceOverviewQuery.isSuccess,
    fetchInvoiceOverviewQuery.isError,
    fetchInvoiceOverviewQuery.data,
    fetchInvoiceOverviewQuery.error,
    fetchInvoiceOverviewQuery.isLoading,
    fetchInvoiceOverviewQuery.isFetching,
    fetchInvoiceOverviewQuery.isRefetching,
  ]);

  useEffect(() => {
    if (applyInvoiceAdjustmentsMutation.isSuccess) {
      setToastOptions({
        open: true,
        message: "Invoice adjustments applied successfully",
        severity: "success",
      });
      setRequestId(applyInvoiceAdjustmentsMutation.data.data);
      setIsStatusModalOpen(true);
    }

    if (applyInvoiceAdjustmentsMutation.isError) {
      setToastOptions({
        open: true,
        message: "Something went wrong",
        severity: "error",
      });
    }
  }, [
    applyInvoiceAdjustmentsMutation.isLoading,
    applyInvoiceAdjustmentsMutation.isSuccess,
    applyInvoiceAdjustmentsMutation.isError,
  ]);

  return (
    <Grid
      container
      direction={"column"}
      spacing={3}
      sx={{
        width: "100%",
        height: "100%",
        minHeight: "75vh",
        direction: "row",
        borderRadius: "10px",
        backgroundColor: "white",
        boxShadow: "0px 0px 4px 0px #00000040",
        p: 3,
        justifyContent: loading ? "center" : "flex-start",
        position: "relative",
      }}
    >
      {loading ? (
        <Grid
          container
          size={12}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <CircularProgress />
        </Grid>
      ) : (
        <>
          {(isInvoiceActionsMutationsLoading ||
            applyInvoiceAdjustmentsMutation.isLoading) && (
            <Loading
              message=" "
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                zIndex: 2,
                backgroundColor: "rgba(255, 255, 255, 0.6)",
              }}
            />
          )}
          <Grid container size={12} direction={"row"}>
            <Grid container size={{ sm: 6, xs: 12 }} direction={"row"}>
              <Grid
                size={12}
                gap={3}
                direction={"row"}
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <KeyboardBackspaceIcon
                  sx={{ transform: "translateY(-3px)", cursor: "pointer" }}
                  onClick={handleReturn}
                />
                <Typography fontSize={"1.4rem"}>
                  Invoice #{params?.invoiceNumber}
                </Typography>
              </Grid>
            </Grid>
            <Grid
              gap={2}
              size={{ sm: 6, xs: 12 }}
              direction={"row"}
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                height: "fit-content",
              }}
            >
              <Grid
                container
                size={{ sm: 6, xs: 12 }}
                justifyContent="flex-end"
                alignItems="center"
              >
                {invoiceOverview && params.invoiceNumber && (
                  <Grid container direction={"row"} gap={1}>
                    {invoiceOverview?.invoiceStatus ===
                      InvoiceStatus.Pending && (
                      <Button
                        icon={DiscountIcon}
                        label={"Apply Discount/Fee"}
                        variantType="primary"
                        sx={{
                          py: "0.2rem",
                          width: {
                            xs: "100%",
                            md: "100%",
                            lg: "fit-content",
                          },
                          minWidth: "160px",
                          borderRadius: 0,
                          minHeight: "33px",
                        }}
                        onClick={() => setIsDiscountModalOpen(true)}
                      />
                    )}

                    <InvoiceActions
                      status={invoiceOverview?.invoiceStatus}
                      hasQuickbooksId={invoiceOverview.quickbooksId !== ""}
                      invoiceNumbers={[params.invoiceNumber]}
                      orgId={orgId}
                      accountId={accountId}
                      handleRefresh={handleRefresh}
                      invoiceStatusTargetActions={
                        user?.roleCode === Role.Customer
                          ? INVOICE_STATUS_TARGET_ACTIONS_CUSTOMER
                          : INVOICE_STATUS_TARGET_ACTIONS_ADMIN
                      }
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
          <Grid size={12} direction={"row"}>
            <Tabs
              defaultTab="No Location"
              tabItems={tabItems}
              onTabChange={setSelectedTab}
              value={selectedTab}
            />
            <Divider sx={{ width: "100%" }} />
          </Grid>
          {params.invoiceNumber &&
            invoiceOverview &&
            (selectedTab === 1 ? (
              <InvoiceOverview
                invoiceOverview={invoiceOverview}
                refetch={fetchInvoiceOverviewQuery.refetch}
                pdfFileLink={pdfFileLink}
              />
            ) : selectedTab === 2 ? (
              <InvoiceHistory />
            ) : selectedTab === 3 ? (
              <InvoiceNotes />
            ) : (
              <></>
            ))}
        </>
      )}
      <Modal
        open={isDiscountModalOpen}
        onClose={() => setIsDiscountModalOpen(false)}
        title={"Apply Discount/Fee"}
        content={
          <AdjustmentsForm
            id={"adjustmentsForm"}
            handleEdit={(data) => {
              handleApplyAdjustments(data);
              setIsDiscountModalOpen(false);
            }}
            defaultValues={invoiceOverview?.invoiceAmount}
          ></AdjustmentsForm>
        }
        form={"adjustmentsForm"}
        primaryButtonText="Apply"
        secondaryButtonText="Cancel"
        onSecondaryButtonClick={() => setIsDiscountModalOpen(false)}
        sx={{
          modal: {
            width: { lg: "50%", xs: "80%", sm: "50%" },
          },
        }}
      />
      <CreateRegenerateInvoiceModal
        open={isStatusModalOpen}
        onClose={() => setIsStatusModalOpen(false)}
        title={"Regenerate Invoice"}
        requestId={requestId}
        orgId={orgId}
        accountId={accountId}
        regenerate={true}
        handleRefresh={() => {
          handleRefresh();
          fetchInvoicePreviewQuery.refetch();
        }}
      />
    </Grid>
  );
};
